// Design tokens — copied once from docs/handoff/README.md "Design tokens", plus derived tints (Q10) and the
// third-party brand marks' official colours (recorded exception, plan §6 Step 3). Every component reads from
// here; no other file in @kk/ui or apps/mobile may contain a hex literal (lint-enforced).

export const colors = {
  turquoise: '#32C3BE', // canvas
  deepTeal: '#0099AB', // links / active
  authGradientStart: '#68C1BD', // intro / auth / graduation only
  authGradientEnd: '#0099AB',
  neutral: '#EEF3F7',
  white: '#FFFFFF', // cards
  ink: '#1D2342',
  slate: '#434C58',
  muted: '#6B7A90',
  quietBorder: '#D6DEE6',
  wasabi: '#B0DD76', // primary CTA
  sun: '#FCBF2B', // progress, current node
  orange: '#FD882F', // streak, coin, Oops header (Oops is orange, never red)
  magenta: '#D94991', // badges and reward moments only
  error: '#F53827', // form errors only
  ctaBorder: '#170909', // 2px CTA border + hard shadow colour
  scrim: 'rgba(0,0,0,0.45)', // modal scrim
  /** README avatar-disc blue, reused by the board for the FROM DEVICE badge and the selected ChoiceCard fill. */
  paleTeal: '#DFF8FF',
} as const;

/**
 * Derived tints (Q10: board colours not in the token list are derived, not added). A few are kept as flats
 * rather than rgba because they paint a whole surface, where compositing against an unknown backdrop would
 * drift; each says which token it was derived from and which board element it draws.
 */
export const tint = {
  sun: 'rgba(252,191,43,0.2)', // P07 auto-approve banner fill; also the board's #FFF4D6 wells (C14 "Toy", C04 item plate, C11 sleepover plate, C05 status pill)
  white20: 'rgba(255,255,255,0.2)', // translucent back disc on the gradient (P02)
  white25: 'rgba(255,255,255,0.25)', // unselected chips on turquoise (P07)
  white40: 'rgba(255,255,255,0.4)', // step-progress bars not yet reached (P03)
  white45: 'rgba(255,255,255,0.45)', // inactive intro dots (P01)
  white80: 'rgba(255,255,255,0.8)', // dashed "Add a child" tile on turquoise (P10)
  white90: 'rgba(255,255,255,0.9)', // step eyebrow on turquoise
  ink12: 'rgba(29,35,66,0.12)', // soft card shadow on the auth canvas (P02)
  ink15: 'rgba(29,35,66,0.15)', // C13/C14 progress segments not yet reached (board rgba(29,35,66,.15)) — ink at 15 %
  /** C12–C14 canvas (board #E8F5D8) and the C04 money-tile art plate: `colors.wasabi` at ~29 % over white, kept flat because it paints the screen. */
  questCanvas: '#E8F5D8',
  /** C18 unearned tile fill, C04/C11 score-locked tile fill (board #F4F7FA): `colors.neutral` lifted toward white. */
  quietFill: '#F4F7FA',
  /** C18 unearned dash + disc ring, C12 locked trail node ring, C04/C11 locked tile border, P13b "More" tile, P13d code box (board #9AA5B4): `colors.muted` lightened. */
  quietInk: '#9AA5B4',
  /** C04 greyed coin on a score-locked tile (board #C9CFD8): `colors.quietBorder` darkened. */
  quietCoin: '#C9CFD8',
  /**
   * **THE PALE TICK IN AN UNDONE CHECK** (Family A §4, 30 Sep, by hex: `#C9D2DC`). The check used to be empty
   * until it was ticked, and the shadow was on the check rather than the tile; now the TILE carries the
   * shadow, the check is flat, and a pale tick shows where the done one will be. Deliberately a shade off
   * `quietCoin` — Evan gave this one as a number, so it is its own token rather than the nearest existing grey.
   */
  paleTick: '#C9D2DC',
  /** C13 choice-B disc (board #8AD7F8): `colors.paleTeal` deepened toward `colors.turquoise`. */
  skyDisc: '#8AD7F8',
  /**
   * **THE PALE HALF OF THE GOAL-HELD STRIPE** (board C09/C11/C30 `#FFE7A3`): `colors.sun` lifted toward white,
   * so the striped section reads as the same money as the solid one rather than as a different thing.
   */
  sunHeld: '#FFE7A3',
  /**
   * **A DONE TILE'S PALE GREEN** (board C09 `#EEF8E1`), and the on-time chip's fill: `colors.wasabi` at about
   * a quarter over white. Distinct from `questCanvas` (#E8F5D8), which paints a whole screen — this one has
   * to read as "finished" beside a wasabi check rather than as the room's colour.
   */
  wasabiPale: '#EEF8E1',
  /** The streak chip's fill (board C09 `#FFE9D6`): `colors.orange` lifted, the Oops colour at rest. */
  orangePale: '#FFE9D6',
  /** C05 code well (board #F4FDFF): `colors.paleTeal` at ~35 % over white, flat because it fills a box. */
  codeWell: '#F4FDFF',
  /** C05 "Waiting for a grown-up" status dot (board #D99609): `colors.sun` darkened for contrast on the sun pill. */
  sunDeep: '#D99609',
  /** C08's deduction well (board #FFF1E6): `colors.orange` at ~10 % over white, flat because it fills a tile. */
  oops: '#FFF1E6',
  /**
   * C01's WHAT GROWS IT saving glyph (board #5B9E2E): `colors.wasabi` darkened, the same move `sunDeep` makes
   * and for the same reason — a 2.6px stroke on a near-white chip needs contrast the CTA fill does not have.
   */
  wasabiDeep: '#5B9E2E',
  /**
   * C12's earned badge disc (board #FFE3EF): `colors.magenta` lifted toward white, the badge colour at rest —
   * magenta's only job is badges and reward moments, and this is its badge.
   */
  magentaPale: '#FFE3EF',
} as const;

/**
 * **A BAND'S OWN WELL: its colour at 20 %**, which is what the board draws behind C01's band pill — `#FFF4D6`
 * against `#FCBF2B` is `colors.sun` at a fifth, the identical relationship `tint.sun` records. Derived rather
 * than listed (Q10) so the five wells cannot drift from the five bands, and kept here because it is the only
 * place in the app allowed to know a colour's channels.
 */
export function bandWell(bandIndex: number): string {
  const hex = bandColors[Math.min(bandColors.length - 1, Math.max(0, bandIndex))]!;
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r},${g},${b},0.2)`;
}

/** Band colours in band order: Building · Growing · Good · Great · Super. */
export const bandColors = ['#FD882F', '#FCBF2B', '#B0DD76', '#32C3BE', '#0099AB'] as const;

/** Avatar disc fills, keyed by neutral avatar id (not by child name). */
export const avatarDisc = {
  glasses: '#DFF8FF', // the cub Elijah uses on the board
  pink: '#FFE1E7', // the cub Ruby uses on the board
  brown: '#F6E3C8',
  cap: '#FFF1D6',
  parent: '#E8F5D8',
} as const;

/** Third-party brand marks drawn in @kk/ui icons (P02/P06 social buttons). */
export const brand = {
  google: ['#4285F4', '#34A853', '#FBBC05', '#EA4335'] as const,
} as const;

export const dark = {
  canvas: '#141A33',
  card: '#1F2747',
  stroke: '#3A4470',
  text: '#EEF3F7',
  secondary: '#A9B4C7',
} as const;

export const radii = {
  badge: 6,
  input: 12,
  chip: 12,
  row: 18, // P07 / P09 / C19 row cards, C18 badge tiles
  banner: 14, // P07 auto-approve banner, P08 note card, P13b/P13c amount tiles, C04 art plate
  button: 16,
  buttonLg: 18,
  card: 20,
  tile: 22, // C14 quiz tile, C11 pre-reader icon plate
  cardLg: 24,
  sheet: 28,
  sheetLg: 32,
  pill: 999,
} as const;

export const strokes = {
  quiet: 1.5, // #D6DEE6
  playful: 2, // #1D2342 or #434C58
  playfulBold: 2.5,
  cta: 2, // #170909
} as const;

/**
 * Hard shadow "0 Npx 0 0 #170909", as a depth scale: sm = 3 (44px CTA, C14 "Next") · md = 4 (56px CTA, C12 done
 * nodes) · lg = 5 (C14 correct tile) · xl = 6 (C12 current node, C13 story card, C05 hero card) · xxl = 8
 * (C15 medal). Press drops the face 2px and collapses the shadow.
 */
export const shadows = { sm: 3, md: 4, lg: 5, xl: 6, xxl: 8, pressDrop: 2 } as const;

/** Minimum hit targets by audience. */
/**
 * Minimum touch targets: 44 parent, 48 child, 56 pre-reader (CLAUDE.md) — and **60 for any child control on a
 * tablet**, which is T02's own number: *"portrait is the phone layout one size up (96px icons, 24px names,
 * 60px checks)."* A tablet is held differently: it rests on a table and is reached for, rather than held in
 * two hands with the thumbs already on the glass.
 */
export const hit = { parent: 44, child: 48, prereader: 56, tablet: 60 } as const;

/**
 * **THE TWO SPACING VALUES EVERY CHILD SCREEN USES, AND THERE ARE ONLY TWO** (board C01, redrawn 23 Sep):
 * *"Every gap on this screen was 10px, so nothing grouped. The score card sat on the greeting, the Today
 * label sat on the score card, and the cards below read as one stack. Now 20px separates sections (greeting,
 * score, Today, what's next) and 10px separates cards inside a section. The build drew its cards 1–2px apart
 * and the score card about 3px under the greeting, which is the same fault made worse. **Build these as two
 * named values, not a margin per card, so the next card added can't pick its own.**"*
 *
 * The artboards reach the same two numbers a different way — a column at `gap:10px` with `margin-top:10px` on
 * every section head, which is 20 above a head and 10 between cards. Two constants say it once instead.
 *
 * **They do not step up for a pre-reader**, unlike type, hit targets and the shell's padding. A pre-reader
 * needs bigger things to read and bigger things to touch; the distance that groups two cards is the same
 * distance either way, and a third and fourth value is how "a margin per card" comes back. C09's shell gap
 * was already 20, so this changes the reader alone.
 */
export const childSpace = {
  /** Between sections: greeting · score · Today · what's next (C01); between C04's four reward groups. */
  section: 20,
  /** Inside one section, between its cards or rows. */
  inner: 10,
} as const;

/** cubic-bezier(.22,1,.36,1); 120 / 200 / 320 ms; bounce only on celebrations (C03, C15, C21). */
export const motion = {
  ease: [0.22, 1, 0.36, 1] as const,
  fast: 120,
  base: 200,
  slow: 320,
} as const;

export const layout = {
  designWidth: 402,
  /**
   * **THE WIDEST A COLUMN OF CONTENT IS EVER DRAWN.** Every screen in this app is one column, drawn at phone
   * width, and until 2026-09-21 nothing capped it — so on an iPad all 58 routes stretched to 976pt and a card
   * became a ribbon the eye has to travel.
   *
   * **It is not `designWidth`.** 402 is the frame the BOARD draws in, and the tablet is explicitly *"the phone
   * layout one size up"* (T02) — so the column is allowed to grow with the type rung rather than being pinned
   * to the phone's exact width. 560 is that: comfortably wider than any phone, and narrow enough that a row's
   * label and its value stay in one glance.
   */
  /**
   * **ONE CAP, AND IT IS T04'S MEASURED 620** (Evan, 2026-09-21). It was 560, chosen before any parent tablet
   * artboard existed: *"Sixty points is not a distinction anyone perceives as intentional, and two caps is two
   * things that drift — we've spent a month on exactly that class."*
   *
   * T04 states where 620 comes from rather than rounding to it: the phone's 402pt content width plus the space
   * a line of parent copy can take before the eye loses the start of the next line.
   */
  contentMax: 620,
  designHeight: 874,
  gutter: 24,
  headerSeam: 32, // header sheet radius 0 0 32 32
  /**
   * **ONE HEADER ON EVERY SCREEN** (Family A, 30 Sep): the same top inset above every band and top bar, over the
   * safe area — it was 6, 12 or 24 depending on who drew the screen.
   */
  headerTop: 12,
  /** The back disc on every screen: 44 white circle, 2 ink ring, 20 arrow (Family A, 30 Sep). */
  backDisc: 44,
  backGlyph: 20,
  stickyCtaBottom: 44,
  /**
   * The parent `TabBar`'s height ABOVE its own bottom padding, which is what a tab-root screen has to leave
   * clear at the end of its scrolling content: `paddingTop 12 + disc 56 + gap 6 + label 12×1.5` = 92. The
   * safe-area inset is NOT in it — the bar pads itself by `max(inset, 12)` and a screen adds the same, which
   * is what `tabBarSpace()` does. Read it through that helper rather than by hand.
   *
   * It has to be a number a screen can read because the bar OVERLAPS the screen rather than sitting beside it:
   * `expo-router/ui`'s `TabSlot` renders its screen container at `flexShrink: 0` and each screen at
   * `height: '100%'`, so the slot is the full height of the tabs group and the bar is drawn on top of its
   * last ~120px. Evan's device walk found three P23 rows under it, "including Delete family account".
   */
  tabBarHeight: 92,
  childTab: 54,
  childTabMax: 60,
  ipadRail: 104,
  ipadMinWidth: 700,
} as const;

/**
 * **HOW FAR TEXT MAY SCALE WHERE IT CANNOT GROW** (Evan, 28 Sep: "where a line can't grow, cap its scaling and list those
 * places"). Everywhere else text follows the phone's size in full: containers grow (`minHeight`, not `height`).
 *  · `tight` — text inside a fixed shape: a count in its circle, a size letter in its square, a tab label, a PIN key.
 *  · `control` — a label on a control whose face is a fixed-height press stack (ChildButton), and the score hero.
 */
/**
 * **A CATEGORY'S OWN WELL** (New Build board 03-2, by hex; Evan, 14 Oct, DECISIONS 40: "colour helps a grown-up find a
 * category at a glance"). Behind a category's picture wherever it is drawn: the picker, P12's row, Check categories and
 * a contract's terms. Keyed by brief 03's kinds (`@kk/domain` CHORE_KINDS).
 */
export const kindTint = {
  selfCare: '#DFF8FF',
  household: '#FFF4D6',
  outdoor: '#EAF6DC',
  petsPlants: '#FFE8D9',
  helpingOthers: '#FFE3EF',
  learning: '#E6EAF7',
} as const;

export const fontScaleCap = { tight: 1.3, control: 1.5 } as const;

export type ColorToken = keyof typeof colors;
export type AvatarDiscId = keyof typeof avatarDisc;
